// addForeignKey — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-foreign-key', foreignKey })` op (DSE2 §3.B2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B2.
//
// Per-engine support (mirrors DSE2's `synthesizeAddForeignKey` rules):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql: optional `mssqlWithNoCheck`. RESTRICT, MATCH, DEFERRABLE rejected.
//   - postgres: full surface — MATCH (FULL/PARTIAL/SIMPLE), DEFERRABLE,
//     all five referential actions.
//   - mysql: SET DEFAULT, MATCH, DEFERRABLE rejected.
//   - oracle: ON UPDATE, RESTRICT, SET DEFAULT, MATCH rejected; DEFERRABLE
//     supported.
//
// Reuses `parseColumnList` from `addPrimaryKey` for comma-separated column input.
import type {
  DdlEdit,
  ForeignKeyDef,
  ReferentialAction,
} from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AddForeignKeyArgs = {
  readonly foreignKey: ForeignKeyDef;
};

export function buildAddForeignKeyEdit(args: AddForeignKeyArgs): DdlEdit {
  return { op: 'add-foreign-key', foreignKey: args.foreignKey };
}

export async function commitAddForeignKey(
  ctx: VqeDdlEditContext,
  args: AddForeignKeyArgs,
): Promise<void> {
  const edit = buildAddForeignKeyEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAddForeignKeySupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getAddForeignKeyUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … ADD CONSTRAINT FOREIGN KEY. '
      + 'Declare the foreign key inline at CREATE TABLE time, or recreate '
      + 'the table with the constraint — use “Edit as SQL” to write the change.'
    );
  }
  return '';
}

export function isMatchSupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

export function isDeferrableSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

export function isMssqlWithNoCheckSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function isOnUpdateSupported(engine: SqlEngine): boolean {
  return engine !== 'oracle';
}

export function isReferentialActionSupported(
  engine: SqlEngine,
  action: ReferentialAction,
): boolean {
  if (engine === 'mssql') return action !== 'restrict';
  if (engine === 'mysql') return action !== 'set-default';
  if (engine === 'oracle') {
    return action === 'cascade' || action === 'set-null';
  }
  return true;
}

export function getEngineFieldUnsupportedTooltip(
  engine: SqlEngine,
  field: 'match' | 'deferrable' | 'mssqlWithNoCheck' | 'onUpdate',
): string {
  switch (field) {
    case 'match':
      return 'MATCH {FULL|PARTIAL|SIMPLE} is supported on PostgreSQL only.';
    case 'deferrable':
      return 'DEFERRABLE INITIALLY is supported on PostgreSQL + Oracle only.';
    case 'mssqlWithNoCheck':
      return 'WITH NOCHECK is a SQL Server-only FOREIGN KEY modifier.';
    case 'onUpdate':
      return 'Oracle has no ON UPDATE referential action.';
  }
  void engine;
  return '';
}

/** Pre-flight validator mirroring DSE2's `validateFk` rules so the form can
 *  surface engine errors before the synthesizer would throw. Returns null
 *  when the spec is acceptable. */
export function validateForeignKeyForEngine(
  engine: SqlEngine,
  fk: ForeignKeyDef,
): string | null {
  if (engine === 'sqlite') return getAddForeignKeyUnsupportedTooltip(engine);
  if (fk.columns.length === 0) {
    return 'FOREIGN KEY requires at least one column.';
  }
  if (fk.refColumns.length === 0) {
    return 'FOREIGN KEY requires at least one referenced column.';
  }
  if (fk.columns.length !== fk.refColumns.length) {
    return `FOREIGN KEY columns (${fk.columns.length}) and refColumns (${fk.refColumns.length}) must have the same length.`;
  }
  if (!fk.refTable || fk.refTable.trim().length === 0) {
    return 'FOREIGN KEY requires a referenced table.';
  }
  if (fk.match !== undefined && !isMatchSupported(engine)) {
    return getEngineFieldUnsupportedTooltip(engine, 'match');
  }
  if (fk.deferrable !== undefined && !isDeferrableSupported(engine)) {
    return getEngineFieldUnsupportedTooltip(engine, 'deferrable');
  }
  if (fk.mssqlWithNoCheck !== undefined && !isMssqlWithNoCheckSupported(engine)) {
    return getEngineFieldUnsupportedTooltip(engine, 'mssqlWithNoCheck');
  }
  if (fk.onUpdate !== undefined && !isOnUpdateSupported(engine)) {
    return getEngineFieldUnsupportedTooltip(engine, 'onUpdate');
  }
  if (fk.onDelete !== undefined && !isReferentialActionSupported(engine, fk.onDelete)) {
    return `${engine} FOREIGN KEY 'onDelete' does not accept '${fk.onDelete}'.`;
  }
  if (fk.onUpdate !== undefined && !isReferentialActionSupported(engine, fk.onUpdate)) {
    return `${engine} FOREIGN KEY 'onUpdate' does not accept '${fk.onUpdate}'.`;
  }
  return null;
}
